import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of, race, timer } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { AuthFacade } from '@cpd-app/state/auth';
import { APP_ROUTES } from '@cpd-app/core-module/constants/routes';
import { ErrorCodeType } from '@cpd-app/shared-module/constants';
import { AccountCreationPromptService } from './account-creation-prompt.service';

/**
 * How long an admin route will wait for the session answer before giving up and
 * letting the ordinary guards decide. Only reached when getSession is genuinely
 * slow or failing; a settled session resolves immediately.
 */
const SESSION_SETTLE_TIMEOUT_MS = 8000;

@Injectable({ providedIn: 'root' })
export class AuthGuardService {
  constructor(
    private authFacade: AuthFacade,
    private router: Router,
    private accountCreationPromptService: AccountCreationPromptService
  ) {}

  /**
   * Waits until the session has actually been restored before an admin route
   * evaluates its guards.
   *
   * The route guards read authorization straight out of the store, but the
   * session is restored over the network. On a cold load of /analytics or
   * /manager the guard could win that race, see "not logged in", and bounce a
   * signed-in admin to the login page — the intermittent logout-on-refresh.
   *
   * Applied only to the admin routes, so learner-facing pages keep their
   * existing (synchronous) behavior and blast radius stays small.
   */
  isSessionSettled(): Observable<boolean> {
    return race(
      this.authFacade.isSessionLoaded$.pipe(filter(Boolean), take(1)),
      // Never hang the route: fall through and let authGuard redirect.
      timer(SESSION_SETTLE_TIMEOUT_MS)
    ).pipe(
      take(1),
      switchMap(() => of(true))
    );
  }

  isUserAuthenticated(
    state: RouterStateSnapshot
  ): Observable<UrlTree | boolean> {
    return this.authFacade.isUserAuthenticated$.pipe(
      map((canActivate) => {
        if (!canActivate) {
          this.authFacade.setLoginWallRedirectUrl(state.url);
          return this.router.createUrlTree([APP_ROUTES.Account.Login]);
        }

        return true;
      })
    );
  }

  // Guards the analytics dashboard: counselor OR either admin role. Uses the
  // counselor-or-above set (not strict COUNCELOR), otherwise this guard would
  // lock COUNCELORADMIN/SUPERADMIN out of /analytics.
  isUserCouncelorRole(): Observable<UrlTree | boolean> {
    return this.authFacade.isCounselorOrAbove$.pipe(
      map((canActivate) => {
        if (!canActivate) {
          return this.router.parseUrl(
            `${APP_ROUTES.Error}/${ErrorCodeType.Unauthorized}`
          );
        }

        return true;
      })
    );
  }

  isUserAdminRole(): Observable<UrlTree | boolean> {
    return this.authFacade.isAdmin$.pipe(
      map((canActivate) => {
        if (!canActivate) {
          return this.router.parseUrl(
            `${APP_ROUTES.Error}/${ErrorCodeType.Unauthorized}`
          );
        }

        return true;
      })
    );
  }

  isUserSuperAdminRole(): Observable<UrlTree | boolean> {
    return this.authFacade.isSuperAdmin$.pipe(
      map((canActivate) => {
        if (!canActivate) {
          return this.router.parseUrl(
            `${APP_ROUTES.Error}/${ErrorCodeType.Unauthorized}`
          );
        }

        return true;
      })
    );
  }

  isOverPasscode(): Observable<UrlTree | boolean> {
    return this.authFacade.isOverPasscode$.pipe(
      map((canActivate) => {
        if (!canActivate) {
          return this.router.createUrlTree([APP_ROUTES.Passcode]);
        }

        return true;
      })
    );
  }

  isUserAuthenticatedForLoginWall(): Observable<boolean> {
    return this.authFacade.isUserAuthenticated$.pipe(
      map((isAuthenticated) => {
        if (!isAuthenticated) {
          this.accountCreationPromptService.openAccountCreationDialog();
          return true;
        }
        return isAuthenticated;
      })
    );
  }
}
